import { HttpStatusCode } from '@angular/common/http';
import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialogRef } from '@angular/material/dialog';
import { ApiService } from '@core/services/api/api.service';
import { ShopService } from '@app/services/shops/shop.service';
import { first } from 'rxjs/operators';
import {
  OP_DASHBOARD_URL,
  ORDER_PROTECTION_BASE,
  OrderProtectionConnectionStatus,
  OrderProtectionConnectionStep,
} from './order-protection-connect.model';

@Component({
  selector: 'order-protection-connect-dialog',
  templateUrl: './order-protection-connect-dialog.component.html',
  styleUrls: ['./order-protection-connect-dialog.component.scss'],
  standalone: false,
})
export class OrderProtectionConnectDialogComponent implements OnInit {
  loading = true;
  submitting = false;
  errorMessage = '';
  notApprovedYet = false;

  showIntro = false;
  showApprove = false;
  showConnected = false;
  isApproved = false;
  collaborationCode?: string;
  storeDomain = '';

  private status?: OrderProtectionConnectionStatus;
  private destroyRef = inject(DestroyRef);

  constructor(
    public dialogRef: MatDialogRef<OrderProtectionConnectDialogComponent, OrderProtectionConnectionStatus | undefined>,
    private apiService: ApiService,
    private shopService: ShopService,
  ) {}

  ngOnInit(): void {
    this.storeDomain = this.shopService.currentShopValue?.name ?? '';
    this.apiService
      .get(`${ORDER_PROTECTION_BASE}/connection-status`)
      .pipe(first(), takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (status: OrderProtectionConnectionStatus) => this.applyStatus(status),
        error: () => this.failInitialLoad(),
      });
  }

  connect(): void {
    this.submitting = true;
    this.errorMessage = '';
    this.apiService
      .post(`${ORDER_PROTECTION_BASE}/request-collaboration`)
      .pipe(first(), takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (status: OrderProtectionConnectionStatus) => this.applyStatus(status),
        error: (err) => this.fail(err, 'Failed to request Order Protection access'),
      });
  }

  finalize(): void {
    this.submitting = true;
    this.errorMessage = '';
    this.notApprovedYet = false;
    this.apiService
      .post(`${ORDER_PROTECTION_BASE}/finalize`)
      .pipe(first(), takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (status: OrderProtectionConnectionStatus) => this.applyStatus(status),
        error: (err) => {
          if (err?.status === HttpStatusCode.Conflict) {
            this.notApprovedYet = true;
            this.submitting = false;
            return;
          }
          this.fail(err, 'Failed to connect Order Protection');
        },
      });
  }

  openDashboard(): void {
    window.open(OP_DASHBOARD_URL, '_blank');
  }

  close(): void {
    this.dialogRef.close(this.status);
  }

  private applyStatus(status: OrderProtectionConnectionStatus): void {
    this.status = status;
    this.loading = false;
    this.submitting = false;
    this.collaborationCode = status.collaborationCode;
    this.showIntro = status.step === OrderProtectionConnectionStep.NOT_CONNECTED;
    this.showApprove =
      status.step === OrderProtectionConnectionStep.PENDING_APPROVAL ||
      status.step === OrderProtectionConnectionStep.APPROVED;
    this.showConnected = status.step === OrderProtectionConnectionStep.CONNECTED;
    this.isApproved = status.step === OrderProtectionConnectionStep.APPROVED;
  }

  private failInitialLoad(): void {
    this.loading = false;
    this.showIntro = true;
    this.errorMessage = 'Could not reach Order Protection. Try connecting again.';
  }

  private fail(err: unknown, fallback: string): void {
    this.submitting = false;
    this.errorMessage = (err as { error?: { error?: string } })?.error?.error || fallback;
  }
}
